/**
 * Timeline Component
 */

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import type {
  ColorAdaptiveValue,
  ColorVariant,
  ComponentAlignment,
  GradientDirection,
  ResponsiveValue,
  SpacingInputValue,
  SpacingUnit,
  TypographyValue,
  VariableSelectValue,
} from '../../types'
import {
  deepMerge,
  parseAlignmentFormat,
  parseColorFormat,
  parseGapFormat,
  parseResponsiveFormat,
  parseSpacingFormat,
  parseToVariableSelect,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import {
  formatFromVariableSelect,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
  formatComponentAlignmentFormat,
  formatColorFormat,
  formatResponsiveValue,
  formatGapFormat,
  formatOrientationFormat,
} from '../../format-utils'
import timelineTemplate from './timeline.template.json'

const TIMELINE_DEFAULT_TEMPLATE = timelineTemplate as unknown as ITimelineComponentSettings

export interface ITimelineComponentSettings {
  // Data settings
  'timeline-data-source': string
  'timeline-data-title': string
  'timeline-data-content': string
  'timeline-data-date': string
  'timeline-data-icon': VariableSelectValue
  'timeline-data-set-limit': ResponsiveValue<number>
  'timeline-data-sort-type': string

  // Design settings
  'timeline-design-width': ResponsiveValue<string[] | number[]>
  'timeline-design-comp-h-align': ResponsiveValue<ComponentAlignment>
  'timeline-design-margin': ResponsiveValue<SpacingInputValue>
  'timeline-design-item-gap': ResponsiveValue<{ value: number; unit: SpacingUnit }>
  'timeline-design-orientation': ResponsiveValue<string>
  'timeline-design-num-items': number
  'timeline-design-pagination': boolean
  'timeline-design-pagination-alignment': string
  'timeline-design-title-lines': ResponsiveValue<number[]>
  'timeline-design-content-lines': ResponsiveValue<number[]>
  'timeline-design-icon-color': any
  'timeline-design-icon-background-color-variant': string
  'timeline-design-timeline-line-color': any
  'timeline-design-title-color': any
  'timeline-design-title-color-solid': ColorAdaptiveValue<ColorVariant>
  'timeline-design-title-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'timeline-design-title-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'timeline-design-title-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'timeline-design-content-color': any
  'timeline-design-content-color-solid': ColorAdaptiveValue<ColorVariant>
  'timeline-design-content-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'timeline-design-content-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'timeline-design-content-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'timeline-design-date-color': any
  'timeline-design-date-color-solid': ColorAdaptiveValue<ColorVariant>
  'timeline-design-date-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'timeline-design-date-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'timeline-design-date-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'timeline-design-group-title-color': any
  'timeline-design-group-title-color-solid': ColorAdaptiveValue<ColorVariant>
  'timeline-design-group-title-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'timeline-design-group-title-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'timeline-design-group-title-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'timeline-design-typography': {
    'timeline-design-content-typography': ResponsiveValue<TypographyValue>
    'timeline-design-date-typography': ResponsiveValue<TypographyValue>
    'timeline-design-group-title-typography': ResponsiveValue<TypographyValue>
    'timeline-design-title-typography': ResponsiveValue<TypographyValue>
  }

  // Actions
  'timeline-action-item-click': BuilderActions
}

export interface ITimelineComponent extends IBuilderComponent {
  componentId: 'timeline'
  groupId: 'list'
  settings: ITimelineComponentSettings
}

/**
 * Timeline AI Input
 */
export interface AiTimelineInput {
  dataSource: string // Required: Table ID
  titleColumn?: string // Column ID for timeline title
  contentColumn?: string // Column ID for timeline content
  dateColumn?: string // Column ID for timeline date
  icon?: string // Icon name (e.g., "chevron_right")
  
  // Sort & Limit
  sortBy?: string // Column ID to sort by
  sortType?: 'asc' | 'desc' // Sort direction
  limit?: number | string // Number of items to display (can be responsive: "10 ta:15 de:20")
  
  // Styling
  width?: string // Format: "w:100%" or "w:100% de:w:60%"
  alignment?: string // Format: "center" or "left"
  margin?: string // Format: "m:16px"
  itemGap?: string // Format: "gap:24px"
  orientation?: string | { phone?: string; tablet?: string; desktop?: string } // "vertical" | "horizontal"
  
  // Colors
  titleColor?: string // Title color
  contentColor?: string // Content color
  dateColor?: string // Date color
  groupTitleColor?: string // Group title color
  iconColor?: string // Icon color
  iconBackgroundColor?: string // Icon background color
  lineColor?: string // Timeline line color
  
  // Typography
  typography?: {
    title?: string // Title typography
    content?: string // Content typography
    date?: string // Date typography
    groupTitle?: string // Group title typography
  }
  
  // Behavior
  numItems?: number // Number of items to display
  showPagination?: boolean // Show pagination
  paginationAlignment?: string // Pagination alignment
  titleLines?: number | string // Title line clamp (can be responsive)
  contentLines?: number | string // Content line clamp (can be responsive)
}

export class TimelineComp {
  getTemplate(): ITimelineComponentSettings {
    return TIMELINE_DEFAULT_TEMPLATE
  }

  /**
   * format timeline component settings to AI timeline input
   */
  format(settings: ITimelineComponentSettings): AiTimelineInput {
    const result: AiTimelineInput = {
      dataSource: settings['timeline-data-source'] || '',
    }

    // Data - Source (required)
    if (settings['timeline-data-source']) {
      result.dataSource = settings['timeline-data-source']
    }

    // Data - Columns
    if (settings['timeline-data-title']) {
      result.titleColumn = settings['timeline-data-title']
    }

    if (settings['timeline-data-content']) {
      result.contentColumn = settings['timeline-data-content']
    }

    if (settings['timeline-data-date']) {
      result.dateColumn = settings['timeline-data-date']
    }

    // Data - Icon
    if (settings['timeline-data-icon']) {
      const icon = formatFromVariableSelect(settings['timeline-data-icon'])
      if (icon && icon.startsWith('icon:')) {
        result.icon = icon.substring(5)
      } else {
        result.icon = icon
      }
    }

    // Data - Sort
    if (settings['timeline-data-sort-type']) {
      result.sortType = settings['timeline-data-sort-type'] as 'asc' | 'desc'
    }

    // Data - Limit
    if (settings['timeline-data-set-limit']) {
      const allSame = Object.values(settings['timeline-data-set-limit']).every((v) => v === Object.values(settings['timeline-data-set-limit'])[0])
      if (allSame) {
        result.limit = Object.values(settings['timeline-data-set-limit'])[0]
      } else {
        result.limit = formatResponsiveValue(settings['timeline-data-set-limit'], (val) => String(val))
      }
    }

    // Design - Width
    if (settings['timeline-design-width']) {
      result.width = formatWidthFormat(settings['timeline-design-width'])
    }

    // Design - Alignment
    if (settings['timeline-design-comp-h-align']) {
      result.alignment = formatComponentAlignmentFormat(settings['timeline-design-comp-h-align'])?.replace(/^h:/, '') || undefined
    }

    // Design - Margin
    if (settings['timeline-design-margin']) {
      result.margin = formatSpacingFormat(settings['timeline-design-margin'])
    }

    // Design - Item Gap
    if (settings['timeline-design-item-gap']) {
      result.itemGap = formatGapFormat(settings['timeline-design-item-gap'])
    }

    // Design - Orientation
    if (settings['timeline-design-orientation']) {
      result.orientation = formatOrientationFormat(settings['timeline-design-orientation'])
    }

    // Design - Num Items
    if (settings['timeline-design-num-items'] !== undefined) {
      result.numItems = settings['timeline-design-num-items']
    }

    // Design - Pagination
    if (settings['timeline-design-pagination'] !== undefined) {
      result.showPagination = settings['timeline-design-pagination']
    }

    if (settings['timeline-design-pagination-alignment']) {
      result.paginationAlignment = settings['timeline-design-pagination-alignment']
    }

    // Design - Title Lines
    if (settings['timeline-design-title-lines']) {
      const allSame = Object.values(settings['timeline-design-title-lines']).every((v) => {
        const arr1 = v as number[]
        const arr2 = Object.values(settings['timeline-design-title-lines'])[0] as number[]
        return arr1.length > 0 && arr2.length > 0 && arr1[0] === arr2[0]
      })
      if (allSame && Object.values(settings['timeline-design-title-lines'])[0]?.length > 0) {
        result.titleLines = (Object.values(settings['timeline-design-title-lines'])[0] as number[])[0]
      } else {
        result.titleLines = formatResponsiveValue(settings['timeline-design-title-lines'], (val) => {
          const arr = val as number[]
          return arr.length > 0 ? String(arr[0]) : '1'
        })
      }
    }

    // Design - Content Lines
    if (settings['timeline-design-content-lines']) {
      const allSame = Object.values(settings['timeline-design-content-lines']).every((v) => {
        const arr1 = v as number[]
        const arr2 = Object.values(settings['timeline-design-content-lines'])[0] as number[]
        return arr1.length > 0 && arr2.length > 0 && arr1[0] === arr2[0]
      })
      if (allSame && Object.values(settings['timeline-design-content-lines'])[0]?.length > 0) {
        result.contentLines = (Object.values(settings['timeline-design-content-lines'])[0] as number[])[0]
      } else {
        result.contentLines = formatResponsiveValue(settings['timeline-design-content-lines'], (val) => {
          const arr = val as number[]
          return arr.length > 0 ? String(arr[0]) : '1'
        })
      }
    }

    // Design - Colors
    if (settings['timeline-design-title-color']) {
      result.titleColor = formatColorFormat({
        colorType: settings['timeline-design-title-color'] as any,
        colorSolid: settings['timeline-design-title-color-solid'],
        colorStartGradient: settings['timeline-design-title-color-start-gradient'],
        colorEndGradient: settings['timeline-design-title-color-end-gradient'],
        colorGradientDirection: settings['timeline-design-title-color-gradient-direction'],
      })
    }

    if (settings['timeline-design-content-color']) {
      result.contentColor = formatColorFormat({
        colorType: settings['timeline-design-content-color'] as any,
        colorSolid: settings['timeline-design-content-color-solid'],
        colorStartGradient: settings['timeline-design-content-color-start-gradient'],
        colorEndGradient: settings['timeline-design-content-color-end-gradient'],
        colorGradientDirection: settings['timeline-design-content-color-gradient-direction'],
      })
    }

    if (settings['timeline-design-date-color']) {
      result.dateColor = formatColorFormat({
        colorType: settings['timeline-design-date-color'] as any,
        colorSolid: settings['timeline-design-date-color-solid'],
        colorStartGradient: settings['timeline-design-date-color-start-gradient'],
        colorEndGradient: settings['timeline-design-date-color-end-gradient'],
        colorGradientDirection: settings['timeline-design-date-color-gradient-direction'],
      })
    }

    if (settings['timeline-design-group-title-color']) {
      result.groupTitleColor = formatColorFormat({
        colorType: settings['timeline-design-group-title-color'] as any,
        colorSolid: settings['timeline-design-group-title-color-solid'],
        colorStartGradient: settings['timeline-design-group-title-color-start-gradient'],
        colorEndGradient: settings['timeline-design-group-title-color-end-gradient'],
        colorGradientDirection: settings['timeline-design-group-title-color-gradient-direction'],
      })
    }

    if (settings['timeline-design-icon-color']) {
      result.iconColor = settings['timeline-design-icon-color'] as string
    }

    if (settings['timeline-design-icon-background-color-variant']) {
      result.iconBackgroundColor = settings['timeline-design-icon-background-color-variant']
    }

    if (settings['timeline-design-timeline-line-color']) {
      result.lineColor = settings['timeline-design-timeline-line-color'] as string
    }

    // Design - Typography
    if (settings['timeline-design-typography']) {
      result.typography = {}
      if (settings['timeline-design-typography']['timeline-design-title-typography']) {
        result.typography.title = formatTypographyFormat(settings['timeline-design-typography']['timeline-design-title-typography'])
      }
      if (settings['timeline-design-typography']['timeline-design-content-typography']) {
        result.typography.content = formatTypographyFormat(settings['timeline-design-typography']['timeline-design-content-typography'])
      }
      if (settings['timeline-design-typography']['timeline-design-date-typography']) {
        result.typography.date = formatTypographyFormat(settings['timeline-design-typography']['timeline-design-date-typography'])
      }
      if (settings['timeline-design-typography']['timeline-design-group-title-typography']) {
        result.typography.groupTitle = formatTypographyFormat(settings['timeline-design-typography']['timeline-design-group-title-typography'])
      }
    }

    return result
  }

  /**
   * parse AI timeline input to timeline component settings
   */
  parse(input: AiTimelineInput): ITimelineComponentSettings {
    const result = deepMerge(TIMELINE_DEFAULT_TEMPLATE, {})

    // Data - Source (required)
    if (input.dataSource) {
      result['timeline-data-source'] = input.dataSource
    }

    // Data - Columns
    if (input.titleColumn) {
      result['timeline-data-title'] = input.titleColumn
    }

    if (input.contentColumn) {
      result['timeline-data-content'] = input.contentColumn
    }

    if (input.dateColumn) {
      result['timeline-data-date'] = input.dateColumn
    }

    if (input.icon) {
      result['timeline-data-icon'] = parseToVariableSelect(input.icon)
    }

    // Data - Sort
    if (input.sortType) {
      result['timeline-data-sort-type'] = input.sortType
    }

    // Data - Limit
    if (input.limit !== undefined) {
      if (typeof input.limit === 'string') {
        result['timeline-data-set-limit'] = parseResponsiveFormat(input.limit, (val) => parseInt(val))
      } else {
        result['timeline-data-set-limit'] = parseResponsiveFormat(input.limit.toString(), (val) => parseInt(val))
      }
    }

    // Design - Width
    if (input.width) {
      result['timeline-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentParsed = parseAlignmentFormat(input.alignment)
      if (alignmentParsed.hAlign) {
        result['timeline-design-comp-h-align'] = alignmentParsed.hAlign
      }
    }

    // Design - Margin
    if (input.margin) {
      result['timeline-design-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Item Gap
    if (input.itemGap) {
      result['timeline-design-item-gap'] = parseGapFormat(input.itemGap)
    }

    // Design - Orientation
    if (input.orientation) {
      if (typeof input.orientation === 'string') {
        result['timeline-design-orientation'] = parseResponsiveFormat(input.orientation, (val) => val)
      } else {
        // Handle object format - use parseResponsiveFormat with a constructed string
        const orientationStr = [
          input.orientation.phone ? `ph:${input.orientation.phone}` : '',
          input.orientation.tablet ? `ta:${input.orientation.tablet}` : '',
          input.orientation.desktop ? `de:${input.orientation.desktop}` : '',
        ]
          .filter(Boolean)
          .join(' ')
        if (orientationStr) {
          result['timeline-design-orientation'] = parseResponsiveFormat(orientationStr, (val) => val)
        }
      }
    }

    // Design - Num Items
    if (input.numItems !== undefined) {
      result['timeline-design-num-items'] = input.numItems
    }

    // Design - Pagination
    if (input.showPagination !== undefined) {
      result['timeline-design-pagination'] = input.showPagination
    }

    if (input.paginationAlignment) {
      result['timeline-design-pagination-alignment'] = input.paginationAlignment
    }

    // Design - Title Lines
    if (input.titleLines !== undefined) {
      if (typeof input.titleLines === 'string') {
        result['timeline-design-title-lines'] = parseResponsiveFormat(input.titleLines, (val) => [parseInt(val)])
      } else {
        result['timeline-design-title-lines'] = parseResponsiveFormat(input.titleLines.toString(), (val) => [parseInt(val)])
      }
    }

    // Design - Content Lines
    if (input.contentLines !== undefined) {
      if (typeof input.contentLines === 'string') {
        result['timeline-design-content-lines'] = parseResponsiveFormat(input.contentLines, (val) => [parseInt(val)])
      } else {
        result['timeline-design-content-lines'] = parseResponsiveFormat(input.contentLines.toString(), (val) => [parseInt(val)])
      }
    }

    // Design - Colors
    if (input.titleColor) {
      const titleColorParsed = parseColorFormat(input.titleColor)
      result['timeline-design-title-color'] = titleColorParsed.colorType
      result['timeline-design-title-color-solid'] = titleColorParsed.colorSolid
      result['timeline-design-title-color-start-gradient'] = titleColorParsed.colorStartGradient
      result['timeline-design-title-color-end-gradient'] = titleColorParsed.colorEndGradient
      result['timeline-design-title-color-gradient-direction'] = titleColorParsed.colorGradientDirection
    }

    if (input.contentColor) {
      const contentColorParsed = parseColorFormat(input.contentColor)
      result['timeline-design-content-color'] = contentColorParsed.colorType
      result['timeline-design-content-color-solid'] = contentColorParsed.colorSolid
      result['timeline-design-content-color-start-gradient'] = contentColorParsed.colorStartGradient
      result['timeline-design-content-color-end-gradient'] = contentColorParsed.colorEndGradient
      result['timeline-design-content-color-gradient-direction'] = contentColorParsed.colorGradientDirection
    }

    if (input.dateColor) {
      const dateColorParsed = parseColorFormat(input.dateColor)
      result['timeline-design-date-color'] = dateColorParsed.colorType
      result['timeline-design-date-color-solid'] = dateColorParsed.colorSolid
      result['timeline-design-date-color-start-gradient'] = dateColorParsed.colorStartGradient
      result['timeline-design-date-color-end-gradient'] = dateColorParsed.colorEndGradient
      result['timeline-design-date-color-gradient-direction'] = dateColorParsed.colorGradientDirection
    }

    if (input.groupTitleColor) {
      const groupTitleColorParsed = parseColorFormat(input.groupTitleColor)
      result['timeline-design-group-title-color'] = groupTitleColorParsed.colorType
      result['timeline-design-group-title-color-solid'] = groupTitleColorParsed.colorSolid
      result['timeline-design-group-title-color-start-gradient'] = groupTitleColorParsed.colorStartGradient
      result['timeline-design-group-title-color-end-gradient'] = groupTitleColorParsed.colorEndGradient
      result['timeline-design-group-title-color-gradient-direction'] = groupTitleColorParsed.colorGradientDirection
    }

    if (input.iconColor) {
      result['timeline-design-icon-color'] = input.iconColor
    }

    if (input.iconBackgroundColor) {
      result['timeline-design-icon-background-color-variant'] = input.iconBackgroundColor
    }

    if (input.lineColor) {
      result['timeline-design-timeline-line-color'] = input.lineColor
    }

    // Design - Typography
    if (input.typography) {
      if (input.typography.title) {
        const titleTypo = parseTypographyFormat(input.typography.title)
        result['timeline-design-typography']['timeline-design-title-typography'] = titleTypo
      }

      if (input.typography.content) {
        const contentTypo = parseTypographyFormat(input.typography.content)
        result['timeline-design-typography']['timeline-design-content-typography'] = contentTypo
      }

      if (input.typography.date) {
        const dateTypo = parseTypographyFormat(input.typography.date)
        result['timeline-design-typography']['timeline-design-date-typography'] = dateTypo
      }

      if (input.typography.groupTitle) {
        const groupTitleTypo = parseTypographyFormat(input.typography.groupTitle)
        result['timeline-design-typography']['timeline-design-group-title-typography'] = groupTitleTypo
      }
    }

    return result
  }
}

